/**
 * The collection day ("session") as this phone holds it. Kept in the drafts table (kind
 * 'gate.session') so it survives reloads without a schema change; the server learns of it only
 * through open_session / confirm_roll / close_session commands.
 */
import { deleteDraft, getDb, getDraft, listDrafts, saveDraft } from '@/offline'
import type { DraftRecord } from '@/offline'

export const SESSION_KIND = 'gate.session'

export interface ConfirmedRoll {
  batch_id: string
  kind: 'BAG' | 'SEAL'
  first_payload: string
  last_payload: string
  first_seq: number
  last_seq: number
  count: number
  confirmed_at: string
  command_id: string
  /** Who confirmed it (the operator, or a supervisor assigning it on the spot). */
  by: string
  spare: boolean
}

export interface GateSession {
  id: string
  code: string
  capture_mode: 'APP' | 'PAPER'
  status: 'OPEN' | 'CLOSED'
  group_id: string
  village_name: string
  operator_id: string
  operator_name: string
  opened_at: string
  closed_at: string | null
  scale_equipment_id: string | null
  scale_label: string | null
  stamp_ok: boolean | null
  /** V1: no scale test (null). */
  test_weight_g: number | null
  test_weight_read_g: number | null
  tags_in_hand: number
  seals_in_hand: number
  rolls: ConfirmedRoll[]
  open_command_id: string
  close_command_id: string | null
  /** F4-B3 paper day being typed in. */
  paper: {
    /** V1: no receipt book is picked (null). */
    book_id: string | null
    book_no: string | null
    /** The paper day (cover page date), ISO instant of the start time. */
    day_opened_at: string
    day_closed_at: string | null
  } | null
}

export async function saveSession(s: GateSession, db = getDb()): Promise<void> {
  await saveDraft<GateSession>(
    { id: s.id, kind: SESSION_KIND, actor_id: s.operator_id, session_id: s.id, data: s },
    db,
  )
}

export async function listSessions(db = getDb()): Promise<GateSession[]> {
  const rows = (await listDrafts<GateSession>(SESSION_KIND, db)) as DraftRecord<GateSession>[]
  return rows.map((r) => r.data)
}

export async function getSession(id: string, db = getDb()): Promise<GateSession | undefined> {
  const row = await getDraft<GateSession>(id, db)
  return row?.kind === SESSION_KIND ? row.data : undefined
}

/** Forget closed sessions older than `days` (their commands stay in the outbox until pruned). */
export async function pruneSessions(days = 30, now = Date.now(), db = getDb()): Promise<void> {
  for (const s of await listSessions(db)) {
    if (s.status === 'CLOSED' && s.closed_at && Date.parse(s.closed_at) < now - days * 86_400_000)
      await deleteDraft(s.id, db)
  }
}

export function openAppSession(list: readonly GateSession[]): GateSession | null {
  return list.find((s) => s.status === 'OPEN' && s.capture_mode === 'APP') ?? null
}

export function openPaperSession(list: readonly GateSession[]): GateSession | null {
  return list.find((s) => s.status === 'OPEN' && s.capture_mode === 'PAPER') ?? null
}
